import React, { useState, useMemo } from 'react';
import moment from 'moment';

import { SpinnerWrapper } from 'components/Spinner/spinner';
import { ButtonsContainer, RectangleButton, RectangleButtonLink } from 'components/Button/rectangleButton';
import { useFreecodeContext, useFreecodeCreatorContext } from 'hooks/useContext';
import CreateWipTestImplModal from './Modals/CreateWipTestImplModal';
import MakeWipTestImplModal from './Modals/MakeWipTestImplModal';
import RevertWipTestImplModal from './Modals/RevertWipTestImplModal';
import { ImplVersionContainer, LanguageHeader, PlusIcon, VersionStatus } from './styles';
import { Title, FormSection, Label } from '../../styles';

export default function FreecodeVersioning() {
  const { test, testImpl } = useFreecodeContext();
  const { implVersions } = useFreecodeCreatorContext();

  const loading = useMemo(() => !implVersions?.length, [implVersions]);
  const hasWip = useMemo(() => implVersions?.every?.((value) => !value.isWIP), [implVersions]);

  const [modalsStatus, setModalsStatus] = useState({
    createWipTestImplModal: false,
    makeWipTestImplModal: false,
    revertWipTestImplModal: false,
  });

  // Display existing test implementation versions
  const renderExistingVersions = () => (
    <>
      <LanguageHeader>Existing versions</LanguageHeader>
      {implVersions.map((implVersion, index) => {
        return (
          <ImplVersionContainer key={index}>
            {implVersion._id !== testImpl._id && (
              <RectangleButtonLink
                small
                floatRight
                href={`/freecode-creator/${test._id}?testImplId=${implVersion._id}`}
              >
                Open
              </RectangleButtonLink>
            )}
            <Label displayBlock>
              {implVersion.isLive && <VersionStatus color="green">Live </VersionStatus>}
              {implVersion.isWIP && <VersionStatus color="orange">Work in progress </VersionStatus>}
              {!implVersion.isLive && !implVersion.isWIP && <VersionStatus color="red">Old </VersionStatus>}
              version, executor {implVersion.executionVersion}
            </Label>
            <Label displayBlock>{implVersion._id}</Label>
            <Label displayBlock>
              Created {moment(implVersion.createdAt).format('YYYY-MM-DD')}, Edited{' '}
              {moment(implVersion.editedAt).format('YYYY-MM-DD')}
            </Label>
          </ImplVersionContainer>
        );
      })}
    </>
  );

  return (
    <FormSection>
      <Title>Versioning</Title>
      <Label>
        Handle versions of the test implementation. There can only be one work in progress version and new versions are
        created from the live version.
      </Label>
      <SpinnerWrapper loading={loading}>
        <ButtonsContainer>
          {hasWip && (
            <RectangleButton
              small
              title="Create a new Work in Progress implementation"
              onClick={() => setModalsStatus({ ...modalsStatus, createWipTestImplModal: true })}
            >
              <PlusIcon /> Create WIP
            </RectangleButton>
          )}
          {!hasWip && (
            <>
              <RectangleButton
                small
                title="Make current WIP the new live implementation"
                onClick={() => setModalsStatus({ ...modalsStatus, makeWipTestImplModal: true })}
              >
                Make WIP live
              </RectangleButton>
              <RectangleButton
                small
                marginLeft
                title="Delete the current WIP"
                onClick={() => setModalsStatus({ ...modalsStatus, revertWipTestImplModal: true })}
              >
                Revert WIP
              </RectangleButton>
            </>
          )}
        </ButtonsContainer>
        {implVersions && renderExistingVersions()}
      </SpinnerWrapper>
      {modalsStatus.createWipTestImplModal && (
        <CreateWipTestImplModal
          testId={test._id}
          closeModal={() => setModalsStatus({ ...modalsStatus, createWipTestImplModal: false })}
        />
      )}
      {modalsStatus.makeWipTestImplModal && (
        <MakeWipTestImplModal
          testId={test._id}
          closeModal={() => setModalsStatus({ ...modalsStatus, makeWipTestImplModal: false })}
        />
      )}
      {modalsStatus.revertWipTestImplModal && (
        <RevertWipTestImplModal
          testId={test._id}
          closeModal={() => setModalsStatus({ ...modalsStatus, revertWipTestImplModal: false })}
        />
      )}
    </FormSection>
  );
}
